import { FormEvent, useState } from "react";
import useFiles, { useFilesSearch } from "files/hooks/useFiles";
import { CircleAlertIcon } from "lucide-react";
import { StringParam, useQueryParam } from "use-query-params";
import { Alert, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { TextOverflow } from "~/components/ui/text-overflow";

interface DeleteFileDialogProps {
  open: boolean;
  handleClose: (deleted: boolean) => void;
  fileKey: string;
  isDirectory: boolean;
}

const confirmationDescription = {
  true: (
    <>
      folder and <b>all of its contents</b>
    </>
  ),
  false: "file",
};

const DeleteFileDialog = ({ open, handleClose, fileKey, isDirectory }: DeleteFileDialogProps) => {
  const [search] = useQueryParam("q", StringParam);
  const { mutate: mutateFiles } = useFiles();
  const { mutate: mutateFilesSearch } = useFilesSearch();
  const filename = fileKey.split("/").at(isDirectory ? -2 : -1);

  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const { VITE_RWB_API } = import.meta.env;
    const resp = await fetch(`${VITE_RWB_API}/file-browser/${encodeURIComponent(fileKey)}`, {
      method: "DELETE",
    });

    if (resp.ok) {
      if (search) {
        await mutateFilesSearch();
      } else {
        await mutateFiles();
      }

      handleClose(true);
    } else {
      setError("Unable to delete file");
    }

    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={() => handleClose(false)}>
      <DialogContent showCloseButton={false} className="pt-4 pb-5">
        <DialogHeader>
          <DialogTitle className="text-base">
            <TextOverflow>{`Delete "${filename}"`}</TextOverflow>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col text-sm gap-11">
          <div className="flex flex-col gap-4">
            {error && (
              <Alert variant="destructive">
                <CircleAlertIcon className="stroke-destructive" />
                <AlertTitle>{error}</AlertTitle>
              </Alert>
            )}
            <span>
              Are you sure you want to delete this {confirmationDescription[`${isDirectory}`]}?
            </span>
          </div>
          <div className="flex gap-2 items-center justify-end">
            <Button type="button" variant="outline" onClick={() => handleClose(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting} variant="destructive">
              Delete
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { DeleteFileDialog, type DeleteFileDialogProps };
